import { Controller } from "@hotwired/stimulus"

const SAVE_DELAY_MS = 500

// Connects to data-controller="cart"
export default class extends Controller {
  static targets = ["quantity", "subtotal", "total", "paymentOption", "stripeFeeAmount"]

  connect() {
    this.recalculate()
  }

  increment(event) {
    const input = this.rowInputFor(event.currentTarget)
    input.value = this.clamp(input, this.valueOf(input) + 1)
    this.recalculate()
    this.scheduleSave(input)
  }

  decrement(event) {
    const input = this.rowInputFor(event.currentTarget)
    input.value = this.clamp(input, this.valueOf(input) - 1)
    this.recalculate()
    this.scheduleSave(input)
  }

  onQuantityInput(event) {
    this.recalculate()
    this.scheduleSave(event.target)
  }

  // Debounced so rapid +/- clicks or typing coalesce into a single save,
  // instead of firing a request (and full-page Turbo swap) on every change.
  scheduleSave(input) {
    clearTimeout(input.saveTimeout)
    input.saveTimeout = setTimeout(() => {
      input.closest("form").requestSubmit()
    }, SAVE_DELAY_MS)
  }

  recalculate() {
    let totalCents = 0

    this.quantityTargets.forEach((input, index) => {
      input.value = this.clamp(input, this.valueOf(input))
      const perUnitCents = this.unitPriceCentsOf(input) + this.installationPriceCentsOf(input)
      // Travel is a single trip, so it's added once per row rather than scaled by quantity.
      const subtotalCents = (perUnitCents * this.valueOf(input)) + this.travelCostCentsOf(input)
      this.subtotalTargets[index].textContent = this.formatMoney(subtotalCents)
      totalCents += subtotalCents
    })

    // Always show what the fee *would* be, even before Stripe is selected,
    // so the customer can compare payment methods upfront - but only add it
    // to the total once Stripe is actually chosen.
    const previewFeeCents = this.stripeFeeCentsFor(totalCents)
    if (this.hasStripeFeeAmountTarget) {
      this.stripeFeeAmountTarget.textContent = this.formatMoney(previewFeeCents)
    }
    if (this.stripeSelected()) {
      totalCents += previewFeeCents
    }

    this.totalTarget.textContent = this.formatMoney(totalCents)
  }

  stripeOption() {
    return this.paymentOptionTargets.find((input) => input.value === "stripe")
  }

  stripeSelected() {
    const option = this.stripeOption()
    return !!option && option.checked
  }

  stripeFeeCentsFor(subtotalCents) {
    const option = this.stripeOption()
    if (!option) return 0

    const percent = parseFloat(option.dataset.stripeFeePercent) || 0
    const fixedCents = parseInt(option.dataset.stripeFeeFixedCents, 10) || 0
    return Math.round(subtotalCents * percent) + fixedCents
  }

  rowInputFor(element) {
    return element.closest("[data-cart-row]").querySelector('[data-cart-target="quantity"]')
  }

  valueOf(input) {
    return parseInt(input.value, 10) || 1
  }

  unitPriceCentsOf(input) {
    return parseInt(input.dataset.unitPriceCents, 10) || 0
  }

  installationPriceCentsOf(input) {
    return parseInt(input.dataset.installationPriceCents, 10) || 0
  }

  travelCostCentsOf(input) {
    return parseInt(input.dataset.travelCostCents, 10) || 0
  }

  clamp(input, quantity) {
    const max = parseInt(input.dataset.max, 10) || 1
    return Math.min(Math.max(quantity, 1), max)
  }

  formatMoney(cents) {
    const euros = (cents / 100).toFixed(2).replace(".", ",")
    return `€${euros}`
  }
}
